import clientService from '../clientService';
import { ContentType } from 'src/api/Api';

export interface SystemTool {
  tool_id: string;
  slug: string;
  name: string;
  description?: string | null;
  module?: string[] | null;
  is_active: boolean;
  trial_limit?: number | null;
  created_at?: string;
  // Fallback aliases for UI compatibility
  tool_name?: string;
  display_name?: string;
}

export interface CreateToolPayload {
  slug: string;
  name: string;
  description?: string;
  module: string[];
  is_active: boolean;
  trial_limit?: number | null;
}

export interface OrganisationTool {
  org_tool_id: string;
  org_id: string;
  tool_id: string;
  status: 'active' | 'suspended' | string;
  config?: Record<string, any> | null;
  provisioned_at?: string;
  provisioned_by?: string;
  tool?: SystemTool | null;
}

export interface ProjectTool {
  project_tool_id: string;
  project_id: string;
  tool_id: string;
  enabled_at?: string;
  enabled_by?: string;
  tool?: SystemTool | null;
}

/**
  List all system catalog tools.
 */
export const getSystemTools = async (): Promise<SystemTool[]> => {
  const response = await clientService.request<any, any>({
    path: `/api/v2/tools`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  const data = response.data;
  if (data && Array.isArray(data.data)) {
    return data.data;
  }
  return Array.isArray(data) ? data : [];
};

/**
  Create a new system tool in the global catalog (Admin only).
 */
export const createSystemTool = async (
  toolData: CreateToolPayload
): Promise<SystemTool> => {
  const response = await clientService.request<SystemTool, any>({
    path: `/api/v2/tools`,
    method: 'POST',
    body: toolData,
    type: ContentType.Json,
    format: 'json',
    secure: true,
  });
  return response.data;
};

/**
  List tools provisioned for an organisation.
 */
export const getOrganisationTools = async (orgId: string): Promise<OrganisationTool[]> => {
  const response = await clientService.request<any, any>({
    path: `/api/v2/organisations/${orgId}/tools`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  const data = response.data;
  if (data && Array.isArray(data.data)) {
    return data.data;
  }
  return Array.isArray(data) ? data : [];
};

/**
  Provision a new tool for an organisation (Admin only).
 */
export const provisionOrganisationTool = async (
  orgId: string,
  toolId: string,
  config?: Record<string, any>
): Promise<OrganisationTool> => {
  const response = await clientService.request<OrganisationTool, any>({
    path: `/api/v2/organisations/${orgId}/tools`,
    method: 'POST',
    body: {
      org_id: orgId,
      tool_id: toolId,
      config: config || {},
    },
    type: ContentType.Json,
    format: 'json',
    secure: true,
  });
  return response.data;
};

/**
  Activate a provisioned organisation tool (Admin only).
 */
export const activateOrganisationTool = async (orgToolId: string): Promise<OrganisationTool> => {
  const response = await clientService.request<OrganisationTool, any>({
    path: `/api/v2/organisations/tools/${orgToolId}/activate`,
    method: 'PATCH',
    format: 'json',
    secure: true,
  });
  return response.data;
};

/**
  Suspend a provisioned organisation tool (Admin only).
 */
export const suspendOrganisationTool = async (orgToolId: string): Promise<OrganisationTool> => {
  const response = await clientService.request<OrganisationTool, any>({
    path: `/api/v2/organisations/tools/${orgToolId}/suspend`,
    method: 'PATCH',
    format: 'json',
    secure: true,
  });
  return response.data;
};

/**
  List tools enabled for a project.
 */
export const getProjectTools = async (projectId: string): Promise<ProjectTool[]> => {
  const response = await clientService.request<any, any>({
    path: `/api/v2/projects/${projectId}/tools`,
    method: 'GET',
    format: 'json',
    secure: true,
  });
  const data = response.data;
  if (data && Array.isArray(data.data)) {
    return data.data;
  }
  return Array.isArray(data) ? data : [];
};

/**
  Enable a tool for a project (Requires active organisation entitlement).
 */
export const enableProjectTool = async (
  projectId: string,
  toolId: string
): Promise<ProjectTool> => {
  const response = await clientService.request<ProjectTool, any>({
    path: `/api/v2/projects/${projectId}/tools`,
    method: 'POST',
    body: {
      tool_id: toolId,
    },
    type: ContentType.Json,
    format: 'json',
    secure: true,
  });
  return response.data;
};

/**
  Disable a tool for a project.
 */
export const disableProjectTool = async (
  projectId: string,
  projectToolId: string
): Promise<void> => {
  await clientService.request<void, any>({
    path: `/api/v2/projects/${projectId}/tools/${projectToolId}`,
    method: 'DELETE',
    secure: true,
  });
};
